/**
 * US states, DC, and the inhabited territories — the second half of a
 * `US-XX` tax jurisdiction.
 *
 * A US tax ID is issued either federally (the IRS, an EIN) or by a state
 * revenue authority, so "United States" alone does not identify the issuer.
 * Every other country does, which is why this list only ever appears once
 * `US` is chosen.
 *
 * Codes are the USPS two-letter abbreviations, which is what the `US-XX`
 * suffix carries.
 */
export interface UsState {
    code: string;
    name: string;
}

export const US_STATES: readonly UsState[] = [
    { code: "AL", name: "Alabama" },
    { code: "AK", name: "Alaska" },
    { code: "AZ", name: "Arizona" },
    { code: "AR", name: "Arkansas" },
    { code: "CA", name: "California" },
    { code: "CO", name: "Colorado" },
    { code: "CT", name: "Connecticut" },
    { code: "DE", name: "Delaware" },
    { code: "DC", name: "District of Columbia" },
    { code: "FL", name: "Florida" },
    { code: "GA", name: "Georgia" },
    { code: "HI", name: "Hawaii" },
    { code: "ID", name: "Idaho" },
    { code: "IL", name: "Illinois" },
    { code: "IN", name: "Indiana" },
    { code: "IA", name: "Iowa" },
    { code: "KS", name: "Kansas" },
    { code: "KY", name: "Kentucky" },
    { code: "LA", name: "Louisiana" },
    { code: "ME", name: "Maine" },
    { code: "MD", name: "Maryland" },
    { code: "MA", name: "Massachusetts" },
    { code: "MI", name: "Michigan" },
    { code: "MN", name: "Minnesota" },
    { code: "MS", name: "Mississippi" },
    { code: "MO", name: "Missouri" },
    { code: "MT", name: "Montana" },
    { code: "NE", name: "Nebraska" },
    { code: "NV", name: "Nevada" },
    { code: "NH", name: "New Hampshire" },
    { code: "NJ", name: "New Jersey" },
    { code: "NM", name: "New Mexico" },
    { code: "NY", name: "New York" },
    { code: "NC", name: "North Carolina" },
    { code: "ND", name: "North Dakota" },
    { code: "OH", name: "Ohio" },
    { code: "OK", name: "Oklahoma" },
    { code: "OR", name: "Oregon" },
    { code: "PA", name: "Pennsylvania" },
    { code: "RI", name: "Rhode Island" },
    { code: "SC", name: "South Carolina" },
    { code: "SD", name: "South Dakota" },
    { code: "TN", name: "Tennessee" },
    { code: "TX", name: "Texas" },
    { code: "UT", name: "Utah" },
    { code: "VT", name: "Vermont" },
    { code: "VA", name: "Virginia" },
    { code: "WA", name: "Washington" },
    { code: "WV", name: "West Virginia" },
    { code: "WI", name: "Wisconsin" },
    { code: "WY", name: "Wyoming" },
    { code: "AS", name: "American Samoa" },
    { code: "GU", name: "Guam" },
    { code: "MP", name: "Northern Mariana Islands" },
    { code: "PR", name: "Puerto Rico" },
    { code: "VI", name: "U.S. Virgin Islands" }
];

/**
 * A tax jurisdiction is one string on the wire (`taxIdJurisdiction`), so a
 * US selection folds its state into a `US-XX` suffix and every other country
 * stays a bare alpha-2 code.
 */
export function composeTaxJurisdiction(country: string | null, state: string | null): string {
    if (!country) return "";
    if (country !== "US") return country;
    return state ? `US-${state}` : "US";
}

/** Inverse of `composeTaxJurisdiction`, for hydrating a saved value. */
export function parseTaxJurisdiction(value: string): {
    country: string | null;
    state: string | null;
} {
    const trimmed = value.trim().toUpperCase();
    if (!trimmed) return { country: null, state: null };
    if (trimmed.startsWith("US-")) {
        const state = trimmed.slice(3);
        return {
            country: "US",
            state: US_STATES.some((s) => s.code === state) ? state : null
        };
    }
    return { country: trimmed, state: null };
}
